import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { type BrandSummary, listBrandsOptions } from "@/api";
import type { ChatComposerProps } from "./ChatComposer";

const byDefaultThenName = (a: BrandSummary, b: BrandSummary) =>
  Number(Boolean(b.isDefault)) - Number(Boolean(a.isDefault)) || a.name.localeCompare(b.name);

/**
 * The chat's brand: the composer's picker and the brand a new chat is created with. Offered only with two or more
 * brands (one is always the company default anyway), as frontend/'s BrandPicker, and never for Build with AI chats
 * (they make Build items, not branded output). Once the chat has started its brand is shown read-only.
 */
export function useChatBrand({
  enabled,
  sessionUuid,
  sessionBrandName,
}: Readonly<{ enabled: boolean; sessionUuid: string | undefined; sessionBrandName: string | null | undefined }>) {
  const [picked, setPicked] = useState<string | null>(null);
  const query = useQuery({
    ...listBrandsOptions({ query: { page_size: 100 } }),
    enabled,
    select: (r): BrandSummary[] => [...r.data].sort(byDefaultThenName),
  });
  const brands = query.data ?? [];
  const picker: ChatComposerProps["brand"] =
    enabled && brands.length >= 2
      ? {
          brands,
          selected: picked,
          onSelect: setPicked,
          fixedName: sessionUuid ? (sessionBrandName ?? null) : undefined,
        }
      : undefined;
  return {
    /** Sent when a new chat is created; null = the company default. */
    brandUuid: enabled && !sessionUuid ? picked : null,
    picker,
  };
}
